// src/components/EstimationQuotation/quotationApi.js
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// Quotation API uses the Innoworx API domain to avoid CORS issues.
const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Helper function to make authenticated API requests
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  try {
    // Get authentication token
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error('No authentication token available');
    }

    // Trim token to remove any whitespace that might cause header parsing issues
    const token = idToken.trim();
    
    // Validate token format (should be a JWT)
    if (!token || token.length < 10) {
      throw new Error('Invalid authentication token format');
    }

    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }

    console.log(`Quotation API: Making ${method} request to: ${url}`);
    console.log('Quotation API: Authorization header format:', `Bearer ${token.substring(0, 20)}...`);
    if (body) {
      console.log('Quotation API: Request payload:', body);
    }

    let response;
    try {
      response = await fetch(url, options);
    } catch (fetchError) {
      // Handle CORS and network errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
        const corsError = new Error('CORS Error: Unable to connect to the API. This may be a preflight (OPTIONS) request issue or backend CORS configuration issue.');
        corsError.isCorsError = true;
        corsError.isPreflightIssue = true;
        corsError.status = 0;
        corsError.method = method;
        corsError.endpoint = endpoint;
        console.error('Quotation API: CORS/Preflight Error Details:', {
          method,
          endpoint,
          url,
          error: fetchError.message,
          suggestion: 'Backend needs to handle OPTIONS requests and return proper CORS headers',
        });
        
        // Log network/fetch errors to logging service
        loggingService.logError(
          'api_error_quotation',
          `Quotation API network/fetch error for ${endpoint}`,
          corsError,
          {
            endpoint,
            method,
            errorType: corsError.name || 'NetworkError',
            isCorsError: true,
            isPreflightIssue: true,
          }
        ).catch(logError => {
          console.warn('[quotationApi] Failed to log error:', logError);
        });
        
        throw corsError;
      }
      
      // Log network/fetch errors to logging service
      loggingService.logError(
        'api_error_quotation',
        `Quotation API network/fetch error for ${endpoint}`,
        fetchError,
        {
          endpoint,
          method,
          errorType: fetchError?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[quotationApi] Failed to log error:', logError);
      });
      
      throw fetchError;
    }

    if (!response.ok) {
      // Read response as text first, then try to parse as JSON
      const errorText = await response.text().catch(() => '');
      let errorData = {};
      
      if (errorText) {
        try {
          errorData = JSON.parse(errorText);
        } catch {
          // If not JSON, use the text as the error message
          errorData = { message: errorText, error: errorText };
        }
      }
      
      console.error(
        `Quotation API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );
      console.error('Quotation API: Full error response:', errorText);
      console.error('Quotation API: Authorization header sent:', `Bearer ${token.substring(0, 20)}...`);

      // Extract error message from various possible response formats
      let errorMessage = 'Unknown error';
      if (errorData.body?.error) {
        errorMessage = errorData.body.error;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (typeof errorText === 'string' && errorText) {
        errorMessage = errorText; // Fallback to raw text if not parsable JSON
      }

      // Special handling for 403 Authorization errors
      if (response.status === 403 && errorMessage.includes('Authorization header')) {
        const authError = new Error(
          `Authentication Error (403): The quotation API endpoint may not be properly configured on the backend to accept the Authorization header format. ` +
          `This appears to be a backend configuration issue. Please contact the backend team to verify: ` +
          `1. The /billing/quotations endpoint is properly configured in API Gateway, ` +
          `2. The Cognito authorizer is correctly set up for this endpoint, ` +
          `3. The Authorization header format is being parsed correctly. ` +
          `Error details: ${errorMessage}`
        );
        authError.status = response.status;
        authError.statusText = response.statusText;
        authError.endpoint = endpoint;
        authError.responseData = errorData;
        authError.isAuthConfigError = true;
        throw authError;
      }

      const error = new Error(`API Error: ${response.status} - ${errorMessage}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_quotation',
        `Quotation API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[quotationApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const data = await response.json();
    console.log('Quotation API: Response:', data);
    return data;
  } catch (error) {
    console.error(`Quotation API: Error in ${method} ${endpoint}:`, error);
    if (error.isCorsError) {
      throw error;
    }
    if (error.status) {
      throw error;
    }
    throw new Error(error.message || 'Network error');
  }
};

/**
 * Clean object by removing undefined, null, and empty string values
 */
const cleanObject = (obj) => {
  if (obj === null || obj === undefined) {
    return undefined;
  }
  if (Array.isArray(obj)) {
    return obj.map(cleanObject).filter((item) => item !== undefined);
  }
  if (typeof obj === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = cleanObject(value);
      if (cleanedValue !== undefined && cleanedValue !== null && cleanedValue !== '') {
        cleaned[key] = cleanedValue;
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }
  return obj;
};

/**
 * Quotation API service
 *
 * POC contact shape for poc_vendor / poc_client:
 * { name?, role?, mobile?, email?, gstin? } — see quotationPocHelpers.js
 */
export const quotationApi = {
  /**
   * Create a new quotation
   * POST /billing/quotations
   * @param {Object} quotationData - Quotation data including site_id, customer_id (optional), items, poc_vendor, poc_client, etc.
   * @returns {Promise<Object>} Created quotation
   */
  createQuotation: async (quotationData) => {
    try {
      const cleanedData = cleanObject(quotationData);
      const response = await makeApiRequest('/billing/quotations', 'POST', cleanedData);
      
      // Log API event for creating quotation
      loggingService.logInfo(
        'api_success_quotation',
        `Successfully created quotation`,
        {
          endpoint: '/billing/quotations',
          method: 'POST',
          siteId: quotationData.site_id,
          customerId: quotationData.customer_id,
          itemsCount: quotationData.items?.length || 0,
        }
      ).catch(logError => {
        console.warn('[quotationApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        quotation: response.quotation,
        next_steps: response.next_steps,
      };
    } catch (error) {
      console.error('Quotation API: Error creating quotation:', error);
      return {
        success: false,
        error: error.message || 'Failed to create quotation',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * List quotations with pagination
   * @param {Object} params - Query parameters (limit, next_token, site_id, start_date, end_date)
   * @returns {Promise<Object>} List of quotations
   */
  listQuotations: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params.next_token) {
        queryParams.append('next_token', params.next_token);
      }
      if (params.site_id) {
        queryParams.append('site_id', params.site_id);
      }
      if (params.start_date) {
        queryParams.append('start_date', params.start_date);
      }
      if (params.end_date) {
        queryParams.append('end_date', params.end_date);
      }

      const queryString = queryParams.toString();
      const endpoint = `/billing/quotations${queryString ? `?${queryString}` : ''}`;

      const response = await makeApiRequest(endpoint, 'GET');
      
      // Log API event for listing quotations
      loggingService.logInfo(
        'api_success_quotation',
        `Successfully listed quotations`,
        {
          endpoint: '/billing/quotations',
          method: 'GET',
          count: response.count || 0,
          ...params,
        }
      ).catch(logError => {
        console.warn('[quotationApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        items: response.items || [],
        next_token: response.next_token || null,
        count: response.count || 0,
      };
    } catch (error) {
      console.error('Quotation API: Error listing quotations:', error);
      return {
        success: false,
        error: error.message || 'Failed to list quotations',
        status: error.status,
        responseData: error.responseData,
        items: [],
        count: 0,
      };
    }
  },

  /**
   * Get a single quotation by ID
   * @param {string} quoteId - Quotation ID (quote_id)
   * @returns {Promise<Object>} Quotation details
   */
  getQuotation: async (quoteId) => {
    try {
      if (!quoteId) {
        throw new Error('Quotation ID is required');
      }
      const response = await makeApiRequest(`/billing/quotations/${quoteId}`, 'GET');
      
      // Log API event for getting quotation
      loggingService.logInfo(
        'api_success_quotation',
        `Successfully fetched quotation ${quoteId}`,
        {
          endpoint: `/billing/quotations/${quoteId}`,
          method: 'GET',
          quoteId,
        }
      ).catch(logError => {
        console.warn('[quotationApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        quotation: response.quotation,
      };
    } catch (error) {
      console.error('Quotation API: Error getting quotation:', error);
      return {
        success: false,
        error: error.message || 'Failed to get quotation',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Update a quotation (partial updates supported)
   * PUT /billing/quotations/{quote_id}
   * @param {string} quoteId - Quotation ID (quote_id)
   * @param {Object} updateData - Fields to update (poc_vendor / poc_client use POC contact shape)
   * @returns {Promise<Object>} Updated quotation
   */
  updateQuotation: async (quoteId, updateData) => {
    try {
      if (!quoteId) {
        throw new Error('Quotation ID is required');
      }
      const cleanedData = cleanObject(updateData);
      const response = await makeApiRequest(`/billing/quotations/${quoteId}`, 'PUT', cleanedData);
      
      // Log API event for updating quotation
      loggingService.logInfo(
        'api_success_quotation',
        `Successfully updated quotation ${quoteId}`,
        {
          endpoint: `/billing/quotations/${quoteId}`,
          method: 'PUT',
          quoteId,
          updatedFields: Object.keys(cleanedData || {}),
        }
      ).catch(logError => {
        console.warn('[quotationApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        quotation: response.quotation,
        next_steps: response.next_steps,
      };
    } catch (error) {
      console.error('Quotation API: Error updating quotation:', error);
      return {
        success: false,
        error: error.message || 'Failed to update quotation',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Delete a quotation
   * @param {string} quoteId - Quotation ID (quote_id)
   * @returns {Promise<Object>} Deletion result
   */
  deleteQuotation: async (quoteId) => {
    try {
      if (!quoteId) {
        throw new Error('Quotation ID is required');
      }
      const response = await makeApiRequest(`/billing/quotations/${quoteId}`, 'DELETE');
      
      // Log API event for deleting quotation
      loggingService.logInfo(
        'api_success_quotation',
        `Successfully deleted quotation ${quoteId}`,
        {
          endpoint: `/billing/quotations/${quoteId}`,
          method: 'DELETE',
          quoteId,
        }
      ).catch(logError => {
        console.warn('[quotationApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        message: response.message,
        quote_id: response.quote_id,
      };
    } catch (error) {
      console.error('Quotation API: Error deleting quotation:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete quotation',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Get presigned URL for quotation document upload
   * @param {string} quoteId - Quotation ID (quote_id)
   * @param {Object} params - Request body (content_type, file_name)
   * @returns {Promise<Object>} Presigned POST data
   */
  presignDocumentUpload: async (quoteId, params = {}) => {
    try {
      if (!quoteId) {
        throw new Error('Quotation ID is required');
      }
      const cleanedParams = cleanObject(params);
      const response = await makeApiRequest(
        `/billing/quotations/${quoteId}/presign`,
        'POST',
        cleanedParams
      );
      return {
        success: true,
        data: response,
        presigned_post: response.presigned_post,
        object_key: response.object_key,
        content_type: response.content_type,
        expires_in: response.expires_in,
      };
    } catch (error) {
      console.error('Quotation API: Error getting presigned URL:', error);
      return {
        success: false,
        error: error.message || 'Failed to get presigned URL',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Upload document to S3 using presigned POST
   * @param {Object} presignedPost - Presigned POST data from presignDocumentUpload
   * @param {File} file - File to upload
   * @returns {Promise<Object>} Upload result
   */
  uploadDocument: async (presignedPost, file) => {
    try {
      if (!presignedPost || !presignedPost.url || !presignedPost.fields) {
        throw new Error('Invalid presigned POST data');
      }
      if (!file) {
        throw new Error('File is required');
      }

      const formData = new FormData();
      Object.entries(presignedPost.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const response = await fetch(presignedPost.url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`S3 upload failed: ${response.status} - ${errorText}`);
      }

      return {
        success: true,
        message: 'Document uploaded successfully',
      };
    } catch (error) {
      console.error('Quotation API: Error uploading document:', error);
      return {
        success: false,
        error: error.message || 'Failed to upload document',
      };
    }
  },
};

export default quotationApi;

